<template>
  <div class="space-y-1">
    <label class="block text-sm font-medium text-gray-700">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>
    
    <!-- Champ unique avec validation dynamique -->
    <div class="relative">
      <input
        type="text"
        :placeholder="validationRule.placeholder"
        v-model="inputValue"
        @input="handleInput"
        @blur="validateInput"
        :maxlength="props.type === 'iban' ? 35 : props.type === 'tva' ? 13 : props.type === 'bic' ? 11 : validationRule.maxLength"
        class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
        :class="hasError ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'"
      />
      
      <!-- Message d'erreur -->
      <p v-if="hasError" class="text-sm text-red-500 mt-2 flex items-center">
        <i class="fas fa-exclamation-circle mr-2"></i>
        {{ validationRule.errorMessage }}
      </p>
      
      <!-- Message de succès -->
      <p v-else-if="isValid && inputValue" class="text-sm text-green-600 mt-2 flex items-center">
        <i class="fas fa-check-circle mr-2"></i>
        Format {{ props.type.toUpperCase() }} valide
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { BankingCodeType, ValidationRule, BankingInfoFieldProps } from '../../types/sellerRegister'

interface Emits {
  (e: 'update:modelValue', value: string): void
  (e: 'validation-changed', field: string, isValid: boolean): void
}

const props = withDefaults(defineProps<BankingInfoFieldProps>(), {
  label: 'Code bancaire ou fiscal',
  required: false,
  helpText: 'Saisissez votre numéro'
})

const emit = defineEmits<Emits>()

// Règles de validation par type
const validationRules: Record<BankingCodeType, ValidationRule> = {
  iban: {
    regex: /^FR[0-9]{2}(?: ?[A-Z0-9]{4}){5} ?[A-Z0-9]{3}$/,
    placeholder: 'FR76 3000 1007 9416 0000 0000 000',
    errorMessage: 'Format IBAN invalide. Exemple : FR76 3000 1007 9416 0000 0000 000',
    maxLength: 27,
    minLength: 27
  },
  tva: {
    regex: /^[A-Z]{2}[0-9]{11}$/,
    placeholder: 'FR12345678901',
    errorMessage: 'Format TVA invalide. Exemple : FR12345678901',
    maxLength: 13,
    minLength: 13
  },
  bic: {
    regex: /^[A-Z0-9]{8}([A-Z0-9]{3})?$/,
    placeholder: 'Code BIC (8 ou 11 caractères)',
    errorMessage: 'Format BIC invalide. Le BIC doit contenir 8 ou 11 caractères alphanumériques.',
    maxLength: 11,
    minLength: 8
  }
}

// État local
const inputValue = ref(props.modelValue)
const hasError = ref(false)
const isValid = ref(false)

// Règle de validation actuelle
const validationRule = computed(() => validationRules[props.type])

// Validation de l'input
const validateInput = () => {
  const value = inputValue.value.trim()
  
  if (!value) {
    hasError.value = false
    isValid.value = false
    // Pour les champs requis, un champ vide n'est pas valide
    emit('validation-changed', props.type, false) // Vide = invalide si requis
    return
  }
  
  // Nettoyage spécifique selon le type
  let cleanValue = value
  if (props.type === 'iban') {
    cleanValue = value.replace(/\s/g, '').toUpperCase()
  } else if (props.type === 'tva' || props.type === 'bic') {
    cleanValue = value.toUpperCase()
  }
  
  // Test de la regex
  const isInputValid = validationRule.value.regex.test(cleanValue)
  
  hasError.value = !isInputValid
  isValid.value = isInputValid
  
  emit('validation-changed', props.type, isInputValid)
}

// Formatage automatique de l'IBAN avec espaces
const formatIban = (value: string) => {
  if (props.type === 'iban') {
    // Supprime tous les espaces existants
    const cleanValue = value.replace(/\s/g, '')
    // Ajoute un espace tous les 4 caractères
    return cleanValue.replace(/(.{4})/g, '$1 ').trim()
  }
  return value
}

// Gestion de la saisie avec formatage automatique
const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value
  
  if (props.type === 'iban') {
    // Pour l'IBAN, on permet de taper jusqu'à 27 caractères + espaces
    const cleanValue = value.replace(/\s/g, '')
    
    // Blocage strict : si on a exactement 27 caractères, on ne peut plus taper
    if (cleanValue.length >= 27) {
      // On force la valeur formatée et on empêche toute modification
      const finalFormatted = formatIban(cleanValue.substring(0, 27))
      inputValue.value = finalFormatted
      return
    }
    
    // Applique le formatage seulement si on a au moins 4 caractères
    if (cleanValue.length >= 4) {
      const formattedValue = formatIban(value)
      inputValue.value = formattedValue
    } else {
      inputValue.value = value
    }
  } else if (props.type === 'tva') {
    // Pour la TVA, on permet de taper jusqu'à 13 caractères
    const cleanValue = value.replace(/\s/g, '')
    
    if (cleanValue.length > 13) {
      // Si on dépasse 13 caractères, on bloque
      return
    }
    
    // Blocage strict : si on a exactement 13 caractères, on ne peut plus taper
    if (cleanValue.length >= 13) {
      // On force la valeur et on empêche toute modification
      inputValue.value = cleanValue.toUpperCase()
      return
    }
    
    inputValue.value = value.toUpperCase()
  } else if (props.type === 'bic') {
    // Pour le BIC français, on permet de taper jusqu'à 11 caractères
    const cleanValue = value.replace(/\s/g, '')
    
    if (cleanValue.length > 11) {
      // Si on dépasse 11 caractères, on bloque
      return
    }
    
    // Blocage strict : si on a exactement 11 caractères, on ne peut plus taper
    if (cleanValue.length >= 11) {
      // On force la valeur et on empêche toute modification
      inputValue.value = cleanValue.toUpperCase()
      return
    }
    
    // Permet la saisie entre 8 et 11 caractères
    inputValue.value = value.toUpperCase()
  }
  
  // Déclenche la validation
  validateInput()
}

// Synchronisation avec le v-model
watch(inputValue, (newValue) => {
  emit('update:modelValue', newValue)
})

// Synchronisation depuis le v-model externe
watch(() => props.modelValue, (newValue) => {
  inputValue.value = newValue
  if (newValue) {
    validateInput()
  }
}, { immediate: true })
</script>
